import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Plus, FileDown, FileText, Check, X, Eye, Search, Trash2,
  Download, Users, BarChart3, RefreshCw, Link2, Paperclip, Unlink,
  Loader2, ChevronDown, ChevronUp, Target, BookOpen
} from 'lucide-react';
import { toast, Toaster } from '../ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  generated: { label: 'Generated', color: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', color: 'bg-yellow-100 text-yellow-700' },
  published: { label: 'Published', color: 'bg-green-100 text-green-700' },
};

const TYPE_OPTIONS = [
  { value: 'checklist', label: 'Checklist' },
  { value: 'template', label: 'Template' },
  { value: 'compliance_tracker', label: 'Compliance Tracker' },
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'calculator_worksheet', label: 'Calculator Worksheet' },
  { value: 'sample_format', label: 'Sample Format' },
  { value: 'due_date_calendar', label: 'Due Date Calendar' },
];

const BUCKET_OPTIONS = [
  'gst', 'payroll', 'fdi_india', 'virtual_cfo', 'compliance_updates',
  'receivables_cash_flow', 'mis_reporting', 'templates_checklists',
  'accounting_outsourcing', 'budgeting', 'finance', 'tax',
];

const INITIAL_FORM = {
  title: '', subtitle: '', type: 'checklist', topic_bucket: 'finance',
  target_audience: 'Indian SME owners', cta_headline: 'Download Your Free Resource',
  cta_description: 'Get instant access to this professionally crafted resource.',
  email_gate_enabled: true, form_fields: ['name', 'email'],
  thank_you_message: 'Thank you! Your download is ready.', related_blog_slug: '',
};

const LeadMagnetDashboard = () => {
  const navigate = useNavigate();
  const [magnets, setMagnets] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState(INITIAL_FORM);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [attachSlug, setAttachSlug] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [mRes, dRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/lead-magnets`),
        fetch(`${BACKEND_URL}/api/admin/lead-magnets/dashboard`),
      ]);
      if (mRes.ok) setMagnets((await mRes.json()).magnets || []);
      if (dRes.ok) setMetrics(await dRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createMagnet = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        const magnetId = data.magnet?.magnet_id;

        // Upload file if selected
        if (uploadFile && magnetId) {
          setUploading(true);
          const formData = new FormData();
          formData.append('file', uploadFile);
          const uploadRes = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${magnetId}/upload`, {
            method: 'POST', body: formData,
          });
          setUploading(false);
          if (uploadRes.ok) {
            toast.success('Lead magnet created with file!');
          } else {
            const err = await uploadRes.json();
            toast.error(`Created but upload failed: ${err.detail || 'Unknown error'}`);
          }
        } else {
          toast.success('Lead magnet created!');
        }
        setShowCreate(false); setForm(INITIAL_FORM); setUploadFile(null); fetchData();
      } else toast.error('Failed to create');
    } catch { toast.error('Network error'); }
  };

  const uploadFileToMagnet = async (magnetId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.xlsx,.xls,.docx,.doc,.csv,.txt,.pptx,.ppt,.zip,.png,.jpg,.jpeg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      toast.info(`Uploading ${file.name}...`);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${magnetId}/upload`, {
          method: 'POST', body: formData,
        });
        if (res.ok) { toast.success('File uploaded!'); fetchData(); }
        else { const err = await res.json(); toast.error(err.detail || 'Upload failed'); }
      } catch { toast.error('Upload failed'); }
      setUploading(false);
    };
    input.click();
  };

  const generatePdf = async (id) => {
    toast.info('Generating PDF...');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${id}/generate-pdf`, { method: 'POST' });
      if (res.ok) { toast.success('PDF generated!'); fetchData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Generation failed'); }
    } catch { toast.error('Network error'); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) { toast.success(`Status: ${newStatus}`); fetchData(); }
    } catch { toast.error('Failed'); }
  };

  const deleteMagnet = async (id) => {
    if (!window.confirm('Delete this lead magnet?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchData(); }
    } catch { toast.error('Failed'); }
  };

  const attachToBlog = async (id) => {
    const slug = attachSlug[id];
    if (!slug) { toast.error('Enter blog slug'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${id}/attach/${slug}`, { method: 'POST' });
      if (res.ok) { toast.success('Attached to blog!'); setAttachSlug(p => ({ ...p, [id]: '' })); fetchData(); }
      else toast.error('Blog not found or failed');
    } catch { toast.error('Failed'); }
  };

  const detach = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lead-magnets/${id}/detach`, { method: 'POST' });
      if (res.ok) { toast.success('Detached'); fetchData(); }
    } catch { toast.error('Failed'); }
  };

  const previewPdf = (slug) => {
    window.open(`${BACKEND_URL}/api/lead-magnets/${slug}/file`, '_blank');
  };

  const filtered = magnets.filter(m =>
    !filter || m.title?.toLowerCase().includes(filter.toLowerCase()) ||
    m.type?.toLowerCase().includes(filter.toLowerCase()) ||
    m.slug?.toLowerCase().includes(filter.toLowerCase())
  );

  const m = metrics || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="lead-magnet-dashboard">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Lead Magnets</h1>
              <p className="text-sm text-gray-500">Generate, manage, and track downloadable assets</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-magnet-btn">
            <Plus className="w-4 h-4 mr-2" /> New Lead Magnet
          </Button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Magnets', value: m.total_magnets || 0, icon: FileText, sub: `${m.published || 0} published` },
            { label: 'Downloads', value: m.total_downloads || 0, icon: Download, sub: `${m.conversion_rate || 0}% conversion` },
            { label: 'Leads Captured', value: m.total_leads || 0, icon: Users, sub: 'Via lead magnets' },
            { label: 'Blogs Missing', value: m.blogs_missing_magnets || 0, icon: BookOpen, sub: `of ${m.total_blogs || 0} published` },
          ].map((s, i) => (
            <Card key={s.label || `lm-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid={`metric-${i}`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className="text-[10px] text-gray-400">{s.sub}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Top Performers */}
        {m.top_performers && m.top_performers.length > 0 && (
          <Card className="mb-6">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-600" /> Top Performers</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-3">
                {m.top_performers.map((tp, i) => (
                  <div key={tp.slug || `tp-${i}`} className="p-3 bg-gray-50 rounded-lg text-center" data-testid={`top-performer-${i}`}>
                    <p className="text-sm font-medium truncate">{tp.title}</p>
                    <p className="text-lg font-bold text-emerald-600">{tp.download_count}</p>
                    <p className="text-[10px] text-gray-400">downloads | {tp.lead_count} leads</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto" data-testid="create-magnet-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mb-10">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Create Lead Magnet</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="magnet-title" />
                  <Input placeholder="Subtitle" value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} data-testid="magnet-subtitle" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="magnet-type">
                      {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Topic Bucket</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm" value={form.topic_bucket}
                      onChange={e => setForm(f => ({ ...f, topic_bucket: e.target.value }))} data-testid="magnet-bucket">
                      {BUCKET_OPTIONS.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Target Audience</label>
                    <Input value={form.target_audience} onChange={e => setForm(f => ({ ...f, target_audience: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="CTA Headline" value={form.cta_headline} onChange={e => setForm(f => ({ ...f, cta_headline: e.target.value }))} data-testid="magnet-cta-headline" />
                  <Input placeholder="Blog slug to attach" value={form.related_blog_slug} onChange={e => setForm(f => ({ ...f, related_blog_slug: e.target.value }))} data-testid="magnet-blog-slug" />
                </div>
                <Textarea placeholder="CTA Description" value={form.cta_description} onChange={e => setForm(f => ({ ...f, cta_description: e.target.value }))} rows={2} />
                <Textarea placeholder="Thank you message" value={form.thank_you_message} onChange={e => setForm(f => ({ ...f, thank_you_message: e.target.value }))} rows={2} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.email_gate_enabled} onChange={e => setForm(f => ({ ...f, email_gate_enabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600" data-testid="magnet-email-gate" />
                  Require email to download (email gate)
                </label>

                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors" data-testid="file-upload-zone">
                  <input
                    type="file"
                    id="magnet-file-upload"
                    className="hidden"
                    accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.txt,.pptx,.ppt,.zip,.png,.jpg,.jpeg"
                    onChange={e => setUploadFile(e.target.files[0] || null)}
                  />
                  <label htmlFor="magnet-file-upload" className="cursor-pointer">
                    <Download className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    {uploadFile ? (
                      <div>
                        <p className="text-sm font-medium text-emerald-700">{uploadFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                        <Button variant="ghost" size="sm" className="mt-2 text-red-500" onClick={(e) => { e.preventDefault(); setUploadFile(null); }}>
                          <X className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Upload your file</p>
                        <p className="text-xs text-gray-500 mt-1">PDF, Excel, Word, CSV, PowerPoint, ZIP — max 10 MB</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={createMagnet} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-magnet-btn">
                  <FileText className="w-4 h-4 mr-2" /> Create
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search lead magnets..." className="pl-9" value={filter} onChange={e => setFilter(e.target.value)} data-testid="magnet-search" />
          </div>
        </div>

        {/* Magnet List */}
        <div className="space-y-3">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading...</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              <FileDown className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No lead magnets yet. Create your first one!</p>
            </CardContent></Card>
          ) : filtered.map(mag => {
            const st = STATUS_MAP[mag.status] || STATUS_MAP.draft;
            const isOpen = expandedId === mag.magnet_id;
            return (
              <Card key={mag.magnet_id} className="hover:shadow-md transition-shadow" data-testid={`magnet-row-${mag.magnet_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <FileDown className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{mag.title}</span>
                          <Badge className={st.color}>{st.label}</Badge>
                          <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{mag.type?.replace(/_/g, ' ')}</Badge>
                        </div>
                        <p className="text-xs text-gray-500">{mag.subtitle}</p>
                        <p className="text-[10px] text-gray-400">
                          slug: {mag.slug} | bucket: {mag.topic_bucket}
                          {mag.related_blog_slug && <> | blog: <span className="text-emerald-600">{mag.related_blog_slug}</span></>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs">
                        <p className="font-bold text-emerald-600">{mag.download_count} downloads</p>
                        <p className="text-gray-400">{mag.lead_count} leads</p>
                      </div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => uploadFileToMagnet(mag.magnet_id)} disabled={uploading}
                          className="text-emerald-600 border-emerald-300" data-testid={`upload-${mag.magnet_id}`}>
                          <Paperclip className="w-3.5 h-3.5 mr-1" /> {mag.original_filename ? 'Replace File' : 'Upload File'}
                        </Button>
                        {mag.status === 'draft' && !mag.storage_path && (
                          <Button size="sm" onClick={() => generatePdf(mag.magnet_id)} className="bg-blue-600 hover:bg-blue-700" data-testid={`generate-${mag.magnet_id}`}>
                            <FileDown className="w-3.5 h-3.5 mr-1" /> Generate PDF
                          </Button>
                        )}
                        {mag.status === 'generated' && (
                          <Button size="sm" onClick={() => updateStatus(mag.magnet_id, 'published')} className="bg-green-600 hover:bg-green-700" data-testid={`publish-${mag.magnet_id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Publish
                          </Button>
                        )}
                        {mag.status === 'published' && (
                          <Button size="sm" variant="outline" onClick={() => previewPdf(mag.slug)} data-testid={`preview-${mag.magnet_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                          </Button>
                        )}
                        {mag.status === 'published' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(mag.magnet_id, 'draft')} className="text-amber-600" data-testid={`unpublish-${mag.magnet_id}`}>
                            Unpublish
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteMagnet(mag.magnet_id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-${mag.magnet_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setExpandedId(isOpen ? null : mag.magnet_id)}>
                          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 pt-4 border-t space-y-3" data-testid={`magnet-details-${mag.magnet_id}`}>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div><span className="text-gray-400">CTA:</span> <span className="font-medium">{mag.cta_headline}</span></div>
                        <div><span className="text-gray-400">Audience:</span> <span className="font-medium">{mag.target_audience}</span></div>
                        <div><span className="text-gray-400">Email gate:</span> <Badge className={mag.email_gate_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>{mag.email_gate_enabled ? 'ON' : 'OFF'}</Badge></div>
                      </div>
                      {mag.original_filename && (
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs" data-testid={`file-info-${mag.magnet_id}`}>
                          <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-emerald-800">{mag.original_filename}</span>
                            {mag.file_size && <span className="text-emerald-600 ml-2">({(mag.file_size / 1024).toFixed(1)} KB)</span>}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Input placeholder="Blog slug to attach" className="text-xs h-8 max-w-xs" value={attachSlug[mag.magnet_id] || ''}
                          onChange={e => setAttachSlug(p => ({ ...p, [mag.magnet_id]: e.target.value }))} />
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => attachToBlog(mag.magnet_id)} data-testid={`attach-${mag.magnet_id}`}>
                          <Paperclip className="w-3 h-3 mr-1" /> Attach
                        </Button>
                        {mag.related_blog_slug && (
                          <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500" onClick={() => detach(mag.magnet_id)} data-testid={`detach-${mag.magnet_id}`}>
                            <Unlink className="w-3 h-3 mr-1" /> Detach
                          </Button>
                        )}
                      </div>
                      {mag.status !== 'draft' && (
                        <Button size="sm" variant="outline" onClick={() => generatePdf(mag.magnet_id)} data-testid={`regenerate-${mag.magnet_id}`}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate PDF
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetDashboard;
